// 5.27 FB-4: the Stripe Express Accounts list is a saved snapshot. It no longer
// reads Stripe on every page load. The `account.updated` webhook and the
// 6-hourly `stripe-express-account-reconcile-6h` job keep `lastCheckedOn`
// current, and a per-row Recheck reads Stripe for one account on demand.

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

// One missed 6-hourly reconcile still counts as fresh. After a day without a
// check the reconcile has missed several runs, so the row is marked stale.
export const STRIPE_EXPRESS_FRESH_MS = 12 * HOUR_MS;
export const STRIPE_EXPRESS_STALE_MS = DAY_MS;

export type StripeExpressFreshnessLevel =
  | "fresh"
  | "aging"
  | "stale"
  | "unknown";

export interface StripeExpressFreshness {
  level: StripeExpressFreshnessLevel;
  label: string;
}

function ageLabel(ageMs: number): string {
  if (ageMs < MINUTE_MS) return "Checked just now";
  if (ageMs < HOUR_MS) {
    return `Checked ${Math.floor(ageMs / MINUTE_MS)} min ago`;
  }
  if (ageMs < 2 * DAY_MS) {
    return `Checked ${Math.floor(ageMs / HOUR_MS)} h ago`;
  }
  return `Checked ${Math.floor(ageMs / DAY_MS)} d ago`;
}

export function describeStripeExpressFreshness(
  lastCheckedOn: string | null | undefined,
  nowMs: number
): StripeExpressFreshness {
  if (!lastCheckedOn) return { level: "unknown", label: "Never checked" };
  const checkedMs = new Date(lastCheckedOn).getTime();
  if (Number.isNaN(checkedMs)) return { level: "unknown", label: "Unknown" };

  // A server clock slightly ahead of the browser must not read as negative.
  const ageMs = Math.max(0, nowMs - checkedMs);
  const label = ageLabel(ageMs);
  const level: StripeExpressFreshnessLevel =
    ageMs < STRIPE_EXPRESS_FRESH_MS
      ? "fresh"
      : ageMs < STRIPE_EXPRESS_STALE_MS
      ? "aging"
      : "stale";
  return { level, label };
}

interface PatchableRow {
  id: number;
  hygienistId: number;
}

export interface StripeExpressRowResultLike<Row> {
  row?: Partial<Row> | null;
  leftList?: boolean;
  active?: boolean;
}

export type StripeExpressRowPatchOutcome =
  | "removed"
  | "updated"
  | "unchanged"
  | "mismatch";

// Apply one action's server result to the single affected row. The list is
// never reloaded for a row action. A result for a different Talent is refused
// rather than written over the clicked row.
export function patchStripeExpressRow<Row extends PatchableRow>(
  rows: Row[],
  targetId: number,
  result: StripeExpressRowResultLike<Row> | null | undefined
): { rows: Row[]; outcome: StripeExpressRowPatchOutcome } {
  const index = rows.findIndex(row => row.id === targetId);
  if (index < 0 || !result) return { rows, outcome: "unchanged" };
  const current = rows[index];
  const serverRow =
    result.row && typeof result.row === "object" ? result.row : null;

  if (
    serverRow &&
    serverRow.hygienistId !== undefined &&
    serverRow.hygienistId !== null &&
    Number(serverRow.hygienistId) !== Number(current.hygienistId)
  ) {
    return { rows, outcome: "mismatch" };
  }

  if (result.leftList === true || result.active === false) {
    return {
      rows: rows.filter((_, rowIndex) => rowIndex !== index),
      outcome: "removed"
    };
  }

  if (!serverRow) return { rows, outcome: "unchanged" };
  const next = rows.slice();
  next[index] = { ...current, ...serverRow, id: current.id } as Row;
  return { rows: next, outcome: "updated" };
}
